import Button from '@mui/material/Button';
import { Link, Tooltip } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { useBoolean } from 'src/hooks/use-boolean';

import { fDateTime } from 'src/utils/format-time';

import Iconify from 'src/components/iconify';
import { ConfirmDialog } from 'src/components/custom-dialog';
import CustomPopover, { usePopover } from 'src/components/custom-popover';

import { IStationeryStockCountListItem } from 'src/types/stationery-stock-count';

type Props = {
  row: IStationeryStockCountListItem;
  onDeleteRow: VoidFunction;
  onEditRow: VoidFunction;
  // Backend restricts edit/delete to the most recently created stock
  // count — this row is only actionable when it's confirmed (by the list
  // view) to be that record. The whole page is already admin-gated, so no
  // additional permission check is needed here.
  isEditable: boolean;
};

export default function StationeryStockCountTableRow({
  row,
  onDeleteRow,
  onEditRow,
  isEditable,
}: Props) {
  const { _id, stockCountCode, countDate, remarks } = row;

  const confirm = useBoolean();

  const popover = usePopover();

  return (
    <>
      <Link
        component={RouterLink}
        href={paths.dashboard.stationeryStockCount.view(_id)}
        sx={{
          textDecoration: 'none',
          color: 'inherit',
          display: 'contents',
        }}
      >
        <TableRow hover>
          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
            {stockCountCode}
          </TableCell>

          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
            {fDateTime(countDate)}
          </TableCell>

          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
            {remarks ?? '-'}
          </TableCell>

          <TableCell align="right" sx={{ px: 1, whiteSpace: 'nowrap' }}>
            <Link
              component={RouterLink}
              href={paths.dashboard.stationeryStockCount.view(_id)}
              mr={2}
            >
              <Button variant="soft" color="primary">
                View More
              </Button>
            </Link>

            {isEditable && (
              <Tooltip title="More options">
                <IconButton
                  color={popover.open ? 'inherit' : 'default'}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    popover.onOpen(e);
                  }}
                >
                  <Iconify icon="eva:more-vertical-fill" />
                </IconButton>
              </Tooltip>
            )}

            {!isEditable && (
              <Tooltip title="Only the most recently created stock count can be edited or deleted">
                <span>
                  <IconButton disabled>
                    <Iconify icon="eva:lock-outline" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </TableCell>
        </TableRow>
      </Link>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        arrow="right-top"
        sx={{ width: 140 }}
      >
        <MenuItem
          onClick={() => {
            onEditRow();
            popover.onClose();
          }}
        >
          <Iconify icon="solar:pen-bold" />
          Edit
        </MenuItem>

        <MenuItem
          onClick={() => {
            confirm.onTrue();
            popover.onClose();
          }}
          sx={{ color: 'error.main' }}
        >
          <Iconify icon="solar:trash-bin-trash-bold" />
          Delete
        </MenuItem>
      </CustomPopover>

      <ConfirmDialog
        open={confirm.value}
        onClose={confirm.onFalse}
        title="Delete Stock Count"
        content={`Are you sure you want to delete ${stockCountCode}? This will reverse its stock effect.`}
        action={
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              confirm.onFalse();
              onDeleteRow();
            }}
          >
            Delete
          </Button>
        }
      />
    </>
  );
}
